import { ChangeEvent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useConversationStoreShallow } from 'src/store/conversationStore';
import * as yup from 'yup';

import { SummarizeConversationMessage } from '@@types/models/CreateReport';
import { TextareaRowsProps } from '@@types/models/Reports';

import { EditMessageDocument } from '@graphql/generated/graphql';

import { handleTextareaRows } from '@utils/handleTextareaRows';

import { useMutation } from '@apollo/client';
import { currentLanguageVar } from '@boot/reactiveVariables';
import {
    EditButton,
    EditMessageField,
    EditWrapper,
} from '@domains/new-conversation/organisms/EditSummaryForm/EditSummaryForm.styled';
import { yupResolver } from '@hookform/resolvers/yup';

interface EditSummaryFormProps {
    message: SummarizeConversationMessage;
    onCallback?: () => void;
}

export const EditSummaryForm = ({
    message,
    onCallback,
}: EditSummaryFormProps) => {
    const [rowInfo, setRowInfo] = useState<TextareaRowsProps>({
        maxRows: 10,
        minRows: 1,
        rows: 1,
    });

    const { t } = useTranslation();
    const { caseUuid } = useParams<{ caseUuid: string }>();

    const { setConversationLoading } = useConversationStoreShallow([
        'setConversationLoading',
    ]);

    const {
        formState: { errors, isSubmitting, isValid },
        handleSubmit,
        register,
        reset,
        setValue,
        watch,
    } = useForm<EditSummaryFormValues>({
        mode: 'onChange',
        resolver: yupResolver(EditSummarySchema),
    });

    const [editMessage, { loading }] = useMutation(EditMessageDocument, {
        context: {
            notifyOnError: true,
        },
        onCompleted: () => {
            setConversationLoading(false);
            onCallback?.();
        },
        onError: () => {},
    });

    const { onChange, ...rest } = register('text');

    const isSameValue = watch('text') === message.text;

    const handleChangeTextarea = (e: ChangeEvent<HTMLTextAreaElement>) => {
        handleTextareaRows(e, rowInfo, setRowInfo);
        void onChange(e);
    };

    const onSubmit = (data: EditSummaryFormValues, textId: string) => {
        reset();
        setConversationLoading(true);
        void editMessage({
            variables: {
                input: {
                    uuid: caseUuid ?? '',
                    messages: [{ uuid: textId, textEdited: data.text }],
                },
                targetLanguage: currentLanguageVar(),
            },
        });
    };

    useEffect(() => {
        setValue('text', message.text, {
            shouldDirty: false,
            shouldValidate: true,
        });
    }, []);

    return (
        <EditWrapper>
            <EditMessageField
                {...rest}
                autoFocus={true}
                className="htmlToImageIgnore"
                defaultValue={message.text}
                error={!!errors.text}
                message={t(errors.text?.message || '')}
                onChange={handleChangeTextarea}
                onFocus={handleChangeTextarea}
                placeholder={t('CHAT_YOUR_MESSAGE')}
                rows={rowInfo.rows}
                testId="mIsV7kO"
            />
            <EditButton
                disabled={isSubmitting || !isValid || isSameValue || loading}
                iconProps={{
                    icon: 'SvgSend24',
                    size: 24,
                }}
                isError={!!errors.text?.message}
                isLoading={loading}
                onClick={handleSubmit((formData) =>
                    onSubmit(formData, message.uuid),
                )}
                testId="button_edit"
                variant="primary"
            />
        </EditWrapper>
    );
};

const EditSummarySchema = yup.object({
    text: yup
        .string()
        .required('FIELD_CANNOT_BE_EMPTY')
        .max(800, 'CHAT_MAX_CHARS'),
});

type EditSummaryFormValues = yup.InferType<typeof EditSummarySchema>;
